import {
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef
} from "react";
import { useHistory } from "react-router";
import { motion } from "motion/react";
import KeyboardEventHandler from "@infinium/react-keyboard-event-handler";
import { useMediaQuery } from "react-responsive";
import useWindowSize from "../../hooks/useWindowSize";
import { useOverlayContext } from "../../functional-components/OverlayContext";
import ErrorBoundary from "../../functional-components/ErrorBoundary";
import { ErrorDisplaySmall } from "../ErrorDisplay";
import Icon from "../Icon";
import SimpleLoadingSpinner from "../SimpleLoadingSpinner";
import classNames from "classnames";
import "./style.scss";

export const OverlayCover = ({ children }: { children: ReactNode }) => {
  const history = useHistory();

  return (
    <>
      <motion.div
        id="overlay-cover"
        aria-hidden
        style={{
          top: "var(--padding-top)"
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={e => {
          if ((e.target as HTMLDivElement).id === "overlay-cover") {
            //console.log("close overlay - click overlay-cover");
            history.goBack();
          }
          e.stopPropagation();
        }}
      ></motion.div>
      {children}
    </>
  );
};

const SuspenseFallback = ({ onSuspeseEnd }: { onSuspeseEnd: () => void }) => {
  useLayoutEffect(() => {
    return () => {
      onSuspeseEnd();
    };
  });

  return <SimpleLoadingSpinner />;
};

const GenericOverlay = ({
  id,
  noPadding = false,
  children
}: {
  id: string;
  noPadding?: boolean;
  children: ReactNode;
}) => {
  const history = useHistory();
  const isSmall = useMediaQuery({ maxWidth: 767.98 });
  const winDim = useWindowSize();
  const { setOverlayOpened } = useOverlayContext();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOverlayOpened(true);
    document.body.style.overflowY = "hidden";
    return () => {
      document.body.style.overflowY = "unset";
      setOverlayOpened(false);
    };
  }, [setOverlayOpened]);

  const docClickHandler = useCallback(
    (e: MouseEvent) => {
      const fullscreenElements = document.getElementsByClassName("fullscreen");
      const modalElements = document.getElementsByClassName("modal");
      if (
        document.getElementById("overlay-content") &&
        !document
          .getElementById("overlay-content")
          ?.contains(e.target as Node) &&
        !document.getElementById("overlay-cover")?.contains(e.target as Node) &&
        !document.getElementById("overlay-close")?.contains(e.target as Node) &&
        !document
          .getElementById("overlay-maximize")
          ?.contains(e.target as Node) &&
        !document
          .getElementById("top-navigation")
          ?.contains(e.target as Node) &&
        !document
          .getElementById("flyout-portal-root")
          ?.contains(e.target as Node) &&
        !document.getElementById("ui-messages")?.contains(e.target as Node) &&
        !(
          fullscreenElements[0] &&
          fullscreenElements[0]?.contains(e.target as Node)
        ) &&
        !(modalElements[0] && modalElements[0]?.contains(e.target as Node))
      ) {
        //console.log("close overlay - click document");
        history.goBack();
      }
    },
    [history]
  );

  const onContentReady = useCallback(() => {
    setTimeout(() => {
      contentRef.current?.getElementsByTagName("a").item(0)?.focus();
    }, 1);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      document.body.addEventListener("click", docClickHandler);
    }, 1);
    onContentReady();
    return () => {
      document.body.removeEventListener("click", docClickHandler);
    };
  }, [docClickHandler, onContentReady]);

  return (
    <>
      <motion.div
        id={id ? id : "overlay-window"}
        role="dialog"
        aria-modal
        className={classNames("overlay-window", { "no-padding": noPadding })}
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        exit={{ opacity: 0, scale: 0.7 }}
        transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
        style={{
          ...(isSmall
            ? {
                top: "var(--padding-top)",
                bottom: 0,
                left: 0,
                right: 0
              }
            : {
                top: "calc(1rem + var(--padding-top))",
                bottom: 16,
                left:
                  winDim.innerWidth > 1400 ? winDim.innerWidth / 2 - 650 : 16,
                right:
                  winDim.innerWidth > 1400 ? winDim.innerWidth / 2 - 650 : 16
              }),
          borderRadius: isSmall ? 0 : "0.25rem"
        }}
      >
        <div className="overlay-content" ref={contentRef}>
          <ErrorBoundary
            boundaryId={"overlay-" + (id ? id : "unspecified")}
            fallback={<ErrorDisplaySmall />}
          >
            <Suspense
              fallback={<SuspenseFallback onSuspeseEnd={onContentReady} />}
            >
              {children}
            </Suspense>
          </ErrorBoundary>
          <KeyboardEventHandler
            handleKeys={["esc"]}
            handleFocusableElements
            onKeyEvent={(key, e) => {
              switch (key) {
                case "esc":
                  history.goBack();
                  e.preventDefault();
                  break;
                default:
              }
            }}
          />
        </div>
        <div
          className="overlay-button-bar"
          style={{
            ...(isSmall
              ? {
                  top: 20,
                  right: 24
                }
              : {
                  top: 28,
                  right: 38
                })
          }}
        >
          <button
            className={classNames("overlay-button", {
              "no-padding": noPadding
            })}
            id={"overlay-close"}
            onClick={() => {
              history.goBack();
            }}
          >
            <Icon name="xmark" />
          </button>
          {!isSmall && (
            <button
              className={classNames("overlay-button", {
                "no-padding": noPadding
              })}
              id={"overlay-maximize"}
              onClick={() => {
                history.replace(history.location.pathname, {
                  ...(history.location.state as object),
                  noOverlay: true
                });
              }}
            >
              <Icon name="expand" />
            </button>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default GenericOverlay;
